/**
 * LoginPage — screen 13 · Log in. EMAIL + PASSWORD, AND NOTHING ELSE.
 *
 * Design: layout/13-log-in-{desktop,mobile-states}.txt (main's Phase 2 card).
 *
 * ── 2026-10-18, THE OWN-STACK INTEGRATION (Lis's decision) ─────────────────────
 * Main's version of this page carried three sign-up features that all ran
 * through Supabase, and on our own stack none of them can work. They are
 * REMOVED from the log-in flow, not hidden — the list is here so they can be
 * rebuilt on our own log-in later:
 *
 *   1. COMPANY WEB ADDRESSES. After sign-in, `routeAfterLogin()`
 *      (src/lib/loginHandoff.ts) asked the `auth-email` edge function
 *      (`login_handoff`) for a 60-second single-use token and sent the person to
 *      `<their-slug>.cbops.app/login/handoff`; signing in on another company's
 *      address signed them out and redirected them. Ran only with
 *      REACT_APP_SIGNUP_ENABLED on.
 *   2. MAGIC LINK. "Forgot password?" and "use a magic link" switched to a form
 *      that called `supabase.auth.signInWithOtp()` and showed "Check your
 *      inbox". Ran with the switch OFF too — it was the only password reset.
 *      Our backend has no reset yet (`/api/v1/auth`: login, register, refresh,
 *      me, password-change), so there is now NO self-service "forgot password".
 *   3. RATE CHECK. Before every password sign-in and magic link,
 *      `supabase.functions.invoke('auth-email', { action: 'login_gate' })`,
 *      read by `classifyLoginGate()` (src/lib/loginGate.ts): a real
 *      `rate_limited` answer showed "Too many attempts…" and blocked. Ran only
 *      with the switch on.
 *   And with them the "New to CBOps? Get started" link (switch on only), which
 *   led into the Supabase sign-up flow.
 *
 * `loginHandoff.ts`, `loginGate.ts` and the `/login/handoff` route are left in
 * the code base untouched; this page simply no longer calls them.
 *
 * Error contract (unchanged):
 *   - wrong email or password: one neutral message, never says which.
 *   - anything thrown on the way (network): "Something went wrong, please try
 *     again."
 */

import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { clearSignedOutReason, peekSignedOutReason } from '../../lib/apiClient';
import SignupBrand from '../public/SignupBrand';
import '../../styles/signup.css';

const GENERIC_ERROR = 'Something went wrong, please try again.';
const NEUTRAL_ERROR = "That didn't work. Check your details and try again.";

export default function LoginPage() {
  const { user, signIn } = useAuth();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  // Why the person is here, when the app signed them out by itself (the refresh
  // token died: lib/apiClient.ts). Read once, then cleared, so a later visit to
  // this page does not repeat it.
  const [notice] = useState(() => peekSignedOutReason());
  useEffect(() => {
    clearSignedOutReason();
  }, []);

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error } = await signIn(email, password);
      if (error) setError(NEUTRAL_ERROR);
      // on success `user` is set and the redirect below takes over
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Signed in: straight to the dashboard on this address.
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="signup-page">
      <header className="signup-page__header">
        <Link to="/" className="signup-page__brand">
          <SignupBrand />
        </Link>
      </header>

      <div className="signup-card">
        <h1>Log in to your workspace</h1>
        <p className="signup-lead">Welcome back.</p>

        {notice && !error && (
          <div className="signup-login__notice" role="status">
            {notice}
          </div>
        )}

        {error && (
          <div className="signup-login__error">
            {error}
          </div>
        )}

        <form onSubmit={handlePasswordSignIn} style={{ marginTop: 28 }}>
          <div className="signup-field">
            <label htmlFor="login-email">Work email</label>
            <input
              id="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@company.com"
            />
          </div>
          <div className="signup-field">
            <label htmlFor="login-password">Password</label>
            <div className="signup-password">
              <input
                id="login-password"
                type={showPw ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="Enter your password"
              />
              <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? 'Hide password' : 'Show password'}>
                {showPw ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
              </button>
            </div>
          </div>

          <button type="submit" className="signup-btn" disabled={loading}>
            {loading ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Loader2 size={16} aria-hidden /> Logging in…</span> : 'Log in'}
          </button>
        </form>

        {/* ⛔ OUTSIDE THE SIGN-UP SWITCH, ON PURPOSE (2026-11-02). The reset pages
            are always reachable; this link is the way in. Before this there was no
            self-service reset at all - the admin's "Reset password" dialog was the
            only route back in - and the page's own header said so. */}
        <p className="signup-hint" style={{ marginTop: 16 }}>
          <Link to="/forgot-password">Forgot your password?</Link>
        </p>
      </div>
    </div>
  );
}
